type AnalyticsPayload = Record<string, unknown>

type AnalyticsEvent = AnalyticsPayload & {
  event: string
  timestamp: string
  path?: string
}

const STORAGE_KEY = "faceshot.analytics.events"
const FUNNEL_STORAGE_KEY = "faceshot.analytics.funnel"
const MAX_EVENTS = 200

type FunnelContextInput = {
  funnelSource?: unknown
  funnelEntryPage?: unknown
  funnelOfferCode?: unknown
  funnelOfferName?: unknown
  funnelOfferType?: unknown
  funnelBillingModel?: unknown
  funnelSelectedPlan?: unknown
  funnelValueMxn?: unknown
}

function getSafeWindow(): Window | null {
  return typeof window === "undefined" ? null : window
}

function normalizeEventName(name: string): string {
  return typeof name === "string" ? name.trim() : ""
}

function normalizePayload(payload: AnalyticsPayload | undefined): AnalyticsPayload {
  if (!payload || typeof payload !== "object") return {}
  return Object.entries(payload).reduce<AnalyticsPayload>((acc, [key, value]) => {
    if (!key || typeof key !== "string") return acc
    if (value === undefined) return acc
    acc[key] = value
    return acc
  }, {})
}

function normalizeStringValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : undefined
}

function normalizeNumberValue(value: unknown): number | undefined {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined
  return Number(parsed.toFixed(2))
}

function getStoredFunnelContext(): AnalyticsPayload {
  const win = getSafeWindow()
  if (!win) return {}

  try {
    const raw = win.sessionStorage.getItem(FUNNEL_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === "object" ? normalizePayload(parsed as AnalyticsPayload) : {}
  } catch {
    return {}
  }
}

function buildFunnelContext(payload: FunnelContextInput): AnalyticsPayload {
  const context: AnalyticsPayload = {}
  const funnelSource = normalizeStringValue(payload.funnelSource)
  const funnelEntryPage = normalizeStringValue(payload.funnelEntryPage)
  const funnelOfferCode = normalizeStringValue(payload.funnelOfferCode)
  const funnelOfferName = normalizeStringValue(payload.funnelOfferName)
  const funnelOfferType = normalizeStringValue(payload.funnelOfferType)
  const funnelBillingModel = normalizeStringValue(payload.funnelBillingModel)
  const funnelSelectedPlan = normalizeStringValue(payload.funnelSelectedPlan)
  const funnelValueMxn = normalizeNumberValue(payload.funnelValueMxn)

  if (funnelSource) context.funnelSource = funnelSource
  if (funnelEntryPage) context.funnelEntryPage = funnelEntryPage
  if (funnelOfferCode) context.funnelOfferCode = funnelOfferCode
  if (funnelOfferName) context.funnelOfferName = funnelOfferName
  if (funnelOfferType) context.funnelOfferType = funnelOfferType
  if (funnelBillingModel) context.funnelBillingModel = funnelBillingModel
  if (funnelSelectedPlan) context.funnelSelectedPlan = funnelSelectedPlan
  if (funnelValueMxn !== undefined) context.funnelValueMxn = funnelValueMxn

  return context
}

function persistEvent(entry: AnalyticsEvent): void {
  const win = getSafeWindow()
  if (!win) return

  try {
    const existing = win.localStorage.getItem(STORAGE_KEY)
    const parsed = existing ? JSON.parse(existing) : []
    const list = Array.isArray(parsed) ? parsed : []
    list.push(entry)
    win.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-MAX_EVENTS)))
  } catch {
    return
  }
}

export function mergeFunnelContext(payload: FunnelContextInput): void {
  const win = getSafeWindow()
  if (!win) return

  const nextContext = {
    ...getStoredFunnelContext(),
    ...buildFunnelContext(payload),
    updatedAt: new Date().toISOString(),
  }

  try {
    win.sessionStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify(nextContext))
  } catch {
    return
  }
}

export function getFunnelContext(): AnalyticsPayload {
  return getStoredFunnelContext()
}

export function clearFunnelContext(): void {
  const win = getSafeWindow()
  if (!win) return

  try {
    win.sessionStorage.removeItem(FUNNEL_STORAGE_KEY)
  } catch {
    return
  }
}

export function trackEvent(name: string, payload?: AnalyticsPayload): void {
  const win = getSafeWindow()
  const eventName = normalizeEventName(name)
  if (!win || !eventName) return

  const entry: AnalyticsEvent = {
    event: eventName,
    timestamp: new Date().toISOString(),
    path: win.location?.pathname || "/",
    ...getStoredFunnelContext(),
    ...normalizePayload(payload),
  }

  const dataLayer = (win as Window & { dataLayer?: AnalyticsEvent[] }).dataLayer
  if (Array.isArray(dataLayer)) {
    dataLayer.push(entry)
  }

  persistEvent(entry)

  try {
    win.dispatchEvent(new CustomEvent("faceshot:analytics", { detail: entry }))
  } catch {
    return
  }
}

export function trackPageView(page: string, payload?: AnalyticsPayload): void {
  const pageName = normalizeEventName(page)
  if (!pageName) return
  trackEvent("page_view", { page: pageName, ...normalizePayload(payload) })
}
